<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, numeric, minValue, maxValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const uiFlags = useMapGetter('inboxes/getUIFlags');

const state = reactive({
  channelName: '',
  sipServer: '',
  sipUsername: '',
  sipPassword: '',
  sipExtension: '',
  sipPort: 8089,
});

const validationRules = {
  channelName: { required },
  sipServer: { required },
  sipUsername: { required },
  sipPassword: { required },
  sipExtension: { required },
  sipPort: { required, numeric, minValue: minValue(1), maxValue: maxValue(65535) },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  channelName: v$.value.channelName?.$error
    ? t('INBOX_MGMT.ADD.SIP.CHANNEL_NAME.ERROR')
    : '',
  sipServer: v$.value.sipServer?.$error
    ? t('INBOX_MGMT.ADD.SIP.SIP_SERVER.ERROR')
    : '',
  sipUsername: v$.value.sipUsername?.$error
    ? t('INBOX_MGMT.ADD.SIP.SIP_USERNAME.ERROR')
    : '',
  sipPassword: v$.value.sipPassword?.$error
    ? t('INBOX_MGMT.ADD.SIP.SIP_PASSWORD.ERROR')
    : '',
  sipExtension: v$.value.sipExtension?.$error
    ? t('INBOX_MGMT.ADD.SIP.SIP_EXTENSION.ERROR')
    : '',
  sipPort: v$.value.sipPort?.$error
    ? t('INBOX_MGMT.ADD.SIP.SIP_PORT.ERROR')
    : '',
}));

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: state.channelName,
      channel: {
        type: 'sip',
        sip_server: state.sipServer,
        sip_username: state.sipUsername,
        sip_password: state.sipPassword,
        sip_extension: state.sipExtension,
        sip_port: state.sipPort,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INBOX_MGMT.ADD.SIP.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.SIP.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.SIP.DESC')"
    />

    <form class="flex flex-col gap-4 flex-wrap mx-0" @submit.prevent="createChannel">
      <Input
        v-model="state.channelName"
        :label="t('INBOX_MGMT.ADD.SIP.CHANNEL_NAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.SIP.CHANNEL_NAME.PLACEHOLDER')"
        :message="formErrors.channelName"
        :message-type="formErrors.channelName ? 'error' : 'info'"
        @blur="v$.channelName?.$touch"
      />

      <Input
        v-model="state.sipServer"
        :label="t('INBOX_MGMT.ADD.SIP.SIP_SERVER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.SIP.SIP_SERVER.PLACEHOLDER')"
        :message="formErrors.sipServer"
        :message-type="formErrors.sipServer ? 'error' : 'info'"
        @blur="v$.sipServer?.$touch"
      />

      <Input
        v-model="state.sipUsername"
        :label="t('INBOX_MGMT.ADD.SIP.SIP_USERNAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.SIP.SIP_USERNAME.PLACEHOLDER')"
        :message="formErrors.sipUsername"
        :message-type="formErrors.sipUsername ? 'error' : 'info'"
        @blur="v$.sipUsername?.$touch"
      />

      <Input
        v-model="state.sipPassword"
        type="password"
        :label="t('INBOX_MGMT.ADD.SIP.SIP_PASSWORD.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.SIP.SIP_PASSWORD.PLACEHOLDER')"
        :message="formErrors.sipPassword"
        :message-type="formErrors.sipPassword ? 'error' : 'info'"
        @blur="v$.sipPassword?.$touch"
      />

      <Input
        v-model="state.sipExtension"
        :label="t('INBOX_MGMT.ADD.SIP.SIP_EXTENSION.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.SIP.SIP_EXTENSION.PLACEHOLDER')"
        :message="formErrors.sipExtension"
        :message-type="formErrors.sipExtension ? 'error' : 'info'"
        @blur="v$.sipExtension?.$touch"
      />

      <Input
        v-model="state.sipPort"
        type="number"
        :label="t('INBOX_MGMT.ADD.SIP.SIP_PORT.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.SIP.SIP_PORT.PLACEHOLDER')"
        :message="formErrors.sipPort"
        :message-type="formErrors.sipPort ? 'error' : 'info'"
        @blur="v$.sipPort?.$touch"
      />

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.SIP.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
